import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  glassMenuItemClassName,
  glassMenuPanelClassName,
} from "@/lib/glassMenu";
import { cn } from "@/lib/utils";

export interface SuggestOption {
  value: string;
  label: string;
  hint?: string;
}

interface KaiSuggestInputProps {
  value: string;
  onChange: (value: string) => void;
  loadOptions: (query: string) => Promise<SuggestOption[]>;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  emptyLabel?: string;
}

/** Combobox that accepts a picked suggestion or free text. */
export function KaiSuggestInput({
  value,
  onChange,
  loadOptions,
  placeholder,
  disabled,
  invalid,
  emptyLabel = "No matches",
}: KaiSuggestInputProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SuggestOption[]>([]);
  const [highlight, setHighlight] = useState(0);
  const requestRef = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || disabled) return;
    const id = ++requestRef.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      loadOptions(value)
        .then((next) => {
          if (requestRef.current !== id) return;
          setOptions(next);
          setHighlight(0);
        })
        .catch(() => {
          if (requestRef.current === id) setOptions([]);
        })
        .finally(() => {
          if (requestRef.current === id) setLoading(false);
        });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [open, value, loadOptions, disabled]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      listRef.current
        ?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]')
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [highlight, open, options.length]);

  function choose(option: SuggestOption) {
    onChange(option.value);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setOpen(true);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && open && options[highlight]) {
            e.preventDefault();
            choose(options[highlight]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid}
        className={cn(
          "h-10 rounded-xl bg-foreground/[0.03]",
          invalid && "border-destructive focus-visible:ring-destructive",
        )}
      />
      {open && !disabled && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          className={cn(
            glassMenuPanelClassName,
            "relative z-10 mt-1 max-h-56 w-full",
          )}
        >
          {loading && options.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" />
              Searching…
            </div>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {value.trim() ? `Use “${value.trim()}”` : emptyLabel}
            </p>
          ) : (
            options.map((opt, index) => (
              <button
                key={`${opt.value}-${index}`}
                type="button"
                role="option"
                aria-selected={highlight === index}
                className={glassMenuItemClassName(highlight === index)}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(opt)}
              >
                <span>{opt.label}</span>
                {opt.hint && (
                  <span className="text-[11px] text-muted-foreground">
                    {opt.hint}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
